import { type ReactNode, useState } from "react";

import { cn } from "#/lib/utils";
import {
	Popover,
	PopoverChevronIcon,
	PopoverContent,
	PopoverTrigger,
} from "./popover";

export type PopoverSelectOption<T extends string> = {
	label: ReactNode;
	value: T;
};

type PopoverSelectProps<T extends string> = {
	align?: "center" | "start" | "end";
	ariaLabel: string;
	contentClassName?: string;
	itemClassName?: string;
	matchTriggerWidth?: boolean;
	onValueChange: (nextValue: T) => void;
	options: ReadonlyArray<PopoverSelectOption<T>>;
	sideOffset?: number;
	showValue?: boolean;
	triggerClassName?: string;
	triggerLabel?: ReactNode;
	value: T;
};

const triggerBaseClassName =
	"flex w-fit cursor-pointer items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-body2 whitespace-nowrap shadow-xs transition-[border-color,color,box-shadow] outline-none hover:border-trade-border-hover focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 [&_[data-slot=select-value]]:line-clamp-1 [&_[data-slot=select-value]]:flex [&_[data-slot=select-value]]:items-center [&_[data-slot=select-value]]:gap-2 [&_[data-slot=select-value]]:transition-colors [&_[data-slot=popover-trigger-label]]:transition-colors [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground data-[state=open]:[&_[data-slot=select-value]]:text-[var(--trade-accent)] data-[state=open]:[&_[data-slot=popover-trigger-label]]:text-[var(--trade-accent)] data-[state=open]:[&_[data-slot=popover-trigger-icon]]:text-[var(--trade-accent)]";

const contentBaseClassName =
	"overflow-hidden border dark:border-[var(--color-theme-line-default)] dark:bg-[var(--color-theme-bg-elevated)] p-0";

const viewportBaseClassName = "p-1";

const itemBaseClassName =
	"relative flex w-full cursor-pointer items-center gap-2 rounded-sm border border-transparent px-2 py-1.5 text-body2 outline-hidden select-none transition-[background-color,border-color,color] hover:border-transparent hover:bg-accent hover:text-accent-foreground dark:hover:bg-[var(--color-theme-fill-strong)] data-[state=checked]:text-[var(--trade-accent)]";

export function PopoverSelect<T extends string>({
	align = "center",
	ariaLabel,
	contentClassName,
	itemClassName,
	matchTriggerWidth = false,
	onValueChange,
	options,
	sideOffset = 8,
	showValue = true,
	triggerClassName,
	triggerLabel,
	value,
}: PopoverSelectProps<T>) {
	const [open, setOpen] = useState(false);

	const selectedOption = options.find((option) => option.value === value);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					type="button"
					aria-label={ariaLabel}
					className={cn(triggerBaseClassName, triggerClassName)}
				>
					{triggerLabel ? (
						<span
							data-slot="popover-trigger-label"
							className="text-label2-medium text-trade-text-secondary"
						>
							{triggerLabel}
						</span>
					) : null}
					{showValue ? (
						<span data-slot="select-value">{selectedOption?.label}</span>
					) : null}
					<PopoverChevronIcon
						open={open}
						className="size-4 text-trade-text-secondary"
					/>
				</button>
			</PopoverTrigger>
			<PopoverContent
				align={align}
				sideOffset={sideOffset}
				className={cn(
					contentBaseClassName,
					matchTriggerWidth &&
						"w-[var(--radix-popper-anchor-width)] min-w-[var(--radix-popper-anchor-width)]",
					contentClassName,
				)}
			>
				<div
					data-slot="select-viewport"
					className={viewportBaseClassName}
					role="listbox"
				>
					{options.map((option) => {
						const isSelected = option.value === value;

						return (
							<button
								key={option.value}
								type="button"
								role="option"
								aria-selected={isSelected}
								data-state={isSelected ? "checked" : "unchecked"}
								className={cn(itemBaseClassName, itemClassName)}
								onClick={() => {
									onValueChange(option.value);
									setOpen(false);
								}}
							>
								{option.label}
							</button>
						);
					})}
				</div>
			</PopoverContent>
		</Popover>
	);
}
